import { createSelector } from '@ngrx/store';

import { FeatureFlagKeys } from '../app-entities/feature-flag/feature-flag-state.model';
import { selectFeatureFlag } from '../app-entities/feature-flag/feature-flag-state.selectors';
import { selectCurrentUser, selectCurrentUserIsImplementer } from '../app-entities/users/users-state.selectors';
import { selectIsSubTypeCoach } from '../app-global/billing/billing-state.selectors';
import { selectCompany } from '../app-global/company/company-state.selectors';
import {
  selectCurrentPerson,
  selectCurrentPersonIsImplementer,
  selectCurrentUserPartner,
} from '../app-global/current-person/current-person.selectors';

/**
 * True if the current user is a partner by either data model: the legacy `person.partner_key`
 * (checked on its own, without `partnerType`) or the new `user.partner` record. The backend omits
 * Refer a Friend (`CustomerReferral`) records from `user.partner`, so having a referral link of your
 * own doesn't count.
 */
export const selectCurrentUserIsReferralPartner = createSelector(
  selectCurrentPerson,
  selectCurrentUserPartner,
  (person, partner) => !!person?.partner_key || !!partner
);

/**
 * True once the user, person and company the eligibility checks read from have loaded. Until then
 * the exclusions all read as false, which would briefly show Refer a Friend to ineligible users.
 * Gated on the company rather than its subscription, since the API also tolerates a missing one.
 */
export const selectReferAFriendEligibilityLoaded = createSelector(
  selectCurrentUser,
  selectCurrentPerson,
  selectCompany,
  (user, person, company) => !!user && !!person && !!company
);

/**
 * Refer a Friend is hidden from coaches, Coach-subtype companies and partners. Mirrors the
 * eligibility checks in the API's `getReferAFriendLink`, which rejects `person.isImplementer`;
 * `user.isImplementer` is checked as well.
 */
export const selectShowReferAFriend = createSelector(
  selectReferAFriendEligibilityLoaded,
  selectFeatureFlag(FeatureFlagKeys.enableReferAFriend),
  selectCurrentUserIsImplementer,
  selectCurrentPersonIsImplementer,
  selectIsSubTypeCoach,
  selectCurrentUserIsReferralPartner,
  (loaded, flagEnabled, isUserImplementer, isPersonImplementer, isSubTypeCoach, isPartner) =>
    loaded && !!flagEnabled && !isUserImplementer && !isPersonImplementer && !isSubTypeCoach && !isPartner
);
